import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorNotice } from '../../components/ErrorNotice';
import { SkeletonText } from '../../components/Skeleton';
import { AuditDetail } from './AuditDetail';
import { AuditSummaryText } from './presentation';
import { useModuleLabel } from './useModuleLabel';
import { useAuditEntries } from './queries';

/**
 * "Letzte Aktivitaeten" auf der Startseite -- F5-02, Anforderung 3.
 *
 * **Dieselbe Darstellung wie in der Auditliste.** Kurzbeschreibung,
 * Bearbeiter, Zeitpunkt, Modul -- gebaut aus demselben Bauplan
 * (`presentation.tsx`). Zwei Darstellungen desselben Eintrags liefen nach der
 * naechsten Aenderung auseinander, und der Leser haette zwei Saetze fuer
 * einen Vorgang.
 *
 * **Sie erscheint nur, wer das Protokoll sehen darf.** Die Kachel haengt an
 * `privacy.manage` wie die Sektion selbst -- das Recht blendet sie aus, die
 * Absicherung liegt beim Server (docs/04, Abschnitt 5). Wer sie nicht sehen
 * darf, bekommt hier nichts, statt eine Kachel voller Fehlermeldungen.
 */
export function RecentActivityCard({ limit = 10 }: { limit?: number } = {}) {
  const { t, i18n } = useTranslation();
  const moduleLabel = useModuleLabel();

  const [openEntry, setOpenEntry] = useState<string | null>(null);
  // Genau so viele, wie die Kachel zeigt. Eine volle Seite zu holen und
  // davon zehn anzuzeigen waere die Liste ueber die Leitung, die niemand
  // ansieht -- und sie liefe unter demselben Zwischenspeicherschluessel wie
  // die Auditliste, die eine ganze Seite braucht.
  const page = useAuditEntries({}, 0, { limit });

  // Gefragt sind zehn, und angezeigt werden hoechstens zehn. Die zweite
  // Grenze ist kein Misstrauen gegen den Server, sondern die Zusage der
  // Kachel: Sie heisst "die letzten zehn" und soll das auch dann halten,
  // wenn die Antwort aus einem Zwischenspeicher mit mehr Zeilen kommt.
  const items = (page.data?.items ?? []).slice(0, limit);

  return (
    <>
      <section className="card">
        <div className="card__header">
          <h2>{t('audit.recentTitle')}</h2>
          <Link to="/app/administration/audit" className="nav__item">
            {t('audit.showAll')}
          </Link>
        </div>

        {page.isPending ? <SkeletonText lines={4} /> : null}
        {page.isError ? <ErrorNotice error={page.error} /> : null}

        {page.data && items.length === 0 ? (
          <p className="table__muted">{t('audit.noRecent')}</p>
        ) : null}

        {items.length > 0 ? (
          <div className="table-wrapper">
            <table className="table">
              <tbody>
                {items.map((entry) => (
                  <tr
                    key={entry.id}
                    data-clickable
                    onClick={() => {
                      setOpenEntry(entry.id);
                    }}
                  >
                    <td>
                      <span className="table__subject">
                        {entry.object_label ? `${entry.object_label}: ` : ''}
                        <AuditSummaryText summary={entry.summary} />
                      </span>
                      <div className="table__muted">
                        {moduleLabel(entry.module)} ·{' '}
                        {entry.actor_name ??
                          t(`history.actor.${entry.actor_type}`, {
                            defaultValue: entry.actor_type,
                          })}
                      </div>
                    </td>
                    <td className="table__muted">
                      {new Date(entry.occurred_at).toLocaleString(i18n.language)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {openEntry ? (
        <AuditDetail
          entryId={openEntry}
          onClose={() => {
            setOpenEntry(null);
          }}
        />
      ) : null}
    </>
  );
}
